import { useMemo } from "react";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useReportHistory } from "@/profitability/query";
import { reportError } from "@/profitability/utils";
import { periodCol, resultCol, statusCol, requestedCol } from "./cols";
const columns = [periodCol, resultCol, statusCol, requestedCol];

export const RunList = ({
  businessId,
  onOpenRun,
}: {
  businessId: string;
  onOpenRun: (runId: string) => void;
}) => {
  const history = useReportHistory(businessId);
  const runs = useMemo(
    () => history.data?.pages.flatMap((page) => page.items) ?? [],
    [history.data],
  );

  // Only when there is nothing to show. A failed poll or a failed refetch on
  // window focus leaves the loaded pages in the cache, and replacing a working
  // list with an error panel over one bad request is worse than showing it.
  if (history.error && !history.data)
    return (
      <EmptyTableMessage>
        <Stack gap="md" align="center">
          <Typography variant="bodyW1" textColor="secondary">
            {reportError(history.error)}
          </Typography>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void history.refetch()}
          >
            Try again
          </Button>
        </Stack>
      </EmptyTableMessage>
    );

  // The period picker lives in the page header, so the empty state points at it
  // rather than repeating the action as a second button.
  if (!history.isPending && !runs.length)
    return (
      <EmptyTableMessage>
        <Stack gap="xs" align="center">
          <Typography variant="subtitleW3">No reports yet</Typography>
          <Typography variant="bodyW3" textColor="secondary">
            Pick a report period up top and Volca builds it for you.
          </Typography>
        </Stack>
      </EmptyTableMessage>
    );

  return (
    <Stack gap="md" fullWidth>
      <Card className="w-full overflow-hidden border-stroke-tertiary shadow-none">
        <SimpleTable
          data={runs}
          columns={columns}
          isLoading={history.isPending}
          getRowId={(run) => `${run.start_date}/${run.end_date}`}
          onRowClick={(run) => onOpenRun(run.id)}
          className="w-full"
          scrollAreaClassName="pr-0"
        />
      </Card>
      {history.hasNextPage && (
        <Stack direction="row" justify="center" fullWidth>
          <Button
            size="sm"
            variant="secondary"
            loading={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            Load more
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
